/* eslint-disable react-hooks/exhaustive-deps */
import { Table, Button, Descriptions } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";

import CreatePayoutModal from "./CreatePayoutModal";
import ViewBankDetailsModal from "./ViewBankDetailsModal";
import ViewPayoutModal from "./ViewPayoutsModal";
import ReferrerEventsViewModal from "./ViewReferrerEventsModal";
import { showErrorNotification, showSuccessNotification } from "../../utils/notification";
import { clamp } from "../../utils/responsiveHelpers";

const ReferralDash = ({ adminConfig, ROOT_URL }) => {
  const [totalNumberOfReferrers, setTotalNumberOfReferrerUser] = useState(0);
  const [totalNumberOfReferredUser, setTotalNumberOfReferreredUser] = useState(0);
  const [totalNumberOfCoursesPurchasedViaReferral, setTotalNumberOfCoursesPurchasedViaReferral] =
    useState(0);
  const [totalPayOutToReferrers, setTotalPayOutToReferrers] = useState(0);
  const [totalRevenueViaReferrals, setTotalRevenueViaReferrals] = useState(0);
  const [referrersList, setReferrersList] = useState([]);

  const [isCreatePayoutModalOpen, setIsCreatePayoutModalOpen] = useState(false);
  const [isViewPayoutModalOpen, setIsViewPayoutModalOpen] = useState(false);

  const [isReferrerEventsModalOpen, setIsReferrerEventsModalOpen] = useState(false);
  const [currentReferrerEventLogs, setCurrentReferrerEventLogs] = useState([]);

  const [isBankDetailsModalOpen, setIsBankDetailsModalOpen] = useState(false);
  const [currentReferrerBankDetails, setCurrentReferrerBankDetails] = useState({});

  const [currentReferrerId, setCurrentReferrerId] = useState("");
  const [currentReferrerPayouts, setCurrentReferrerPayouts] = useState([]);
  const [maxAmount, setMaxAmount] = useState(0);

  const fetchReferralStatistics = async () => {
    axios
      .get(`${ROOT_URL}/admin/referral-dashboard/get-referrers-statistics`)
      .then(({ data }) => {
        const {
          totalNumberOfReferrerUsers = 0,
          totalNumberOfReferredUser = 0,
          totalCoursesPurchasedByReferredUser = 0,
          totalRevenue = 0,
          totalPaymentToReferrers = 0
        } = data;

        setTotalNumberOfReferrerUser(totalNumberOfReferrerUsers);
        setTotalNumberOfReferreredUser(totalNumberOfReferredUser);
        setTotalNumberOfCoursesPurchasedViaReferral(totalCoursesPurchasedByReferredUser);
        setTotalRevenueViaReferrals(totalRevenue);
        setTotalPayOutToReferrers(totalPaymentToReferrers);
        showSuccessNotification("Referrer Statistics fetched successfully");
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchReferrersData = async () => {
    await axios
      .get(`${ROOT_URL}/admin/referral-dashboard/get-referrers-data`)
      .then((res) => {
        const { referrersList } = res.data;
        setReferrersList(referrersList);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchReferrerPayoutData = async (referrerId) => {
    axios
      .get(`${ROOT_URL}/admin/referral-dashboard/get-referrer-payout`, {
        params: { referrerId }
      })
      .then(({ data }) => {
        setCurrentReferrerPayouts(data.payoutData);
        showSuccessNotification("Payouts fetched successfully");
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchReferrerEventsData = async (referrerId) => {
    axios
      .get(`${ROOT_URL}/admin/referral-dashboard/get-referred-users-events`, {
        params: { referrerId }
      })
      .then(({ data }) => {
        const { eventLogs } = data;
        setCurrentReferrerEventLogs(eventLogs);
        setIsReferrerEventsModalOpen(true);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleDeletePayout = async (values) => {
    const { payoutId, deletionReason } = values;
    axios
      .delete(`${ROOT_URL}/admin/referral-dashboard/delete-payout`, {
        params: { payoutId, deletionReason }
      })
      .then(() => {
        fetchReferrerPayoutData(currentReferrerId);
        fetchReferralStatistics();
        showSuccessNotification("Payout deleted successfully");
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleCreatePayout = async (values) => {
    const { amount, transactionId } = values;
    axios
      .post(`${ROOT_URL}/admin/referral-dashboard/create-payout`, {
        userId: currentReferrerId,
        amount,
        transactionId
      })
      .then(() => {
        setCurrentReferrerId("");
        showSuccessNotification("Payout created successfully");
        fetchReferralStatistics();
        setIsCreatePayoutModalOpen(false);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  useEffect(() => {
    fetchReferralStatistics();
    fetchReferrersData();
  }, []);

  const columns = [
    {
      title: "S No.",
      dataIndex: "index",
      key: "index"
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => {
        if (typeof a.name === "string" && typeof b.name === "string") {
          return a.name?.localeCompare(b.name) | 0;
        }
        // Fallback comparison if not string (handle the case if one or both values are not strings)
        return 0;
      }
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => {
        if (typeof a.email === "string" && typeof b.email === "string") {
          return a.email?.localeCompare(b.email) | 0;
        }
        // Fallback comparison if not string (handle the case if one or both values are not strings)
        return 0;
      }
    },
    {
      title: "Total Sign ups",
      dataIndex: "totalSignUps",
      key: "totalSignUps",
      sorter: (a, b) => a.totalSignUps - b.totalSignUps
    },
    {
      title: "Total Purchases",
      dataIndex: "referredUsersPurchasesCount",
      key: "referredUsersPurchasesCount",
      sorter: (a, b) => a.referredUsersPurchasesCount - b.referredUsersPurchasesCount
    },
    {
      title: "Total Revenue",
      key: "referrerDrivenRevenue",
      dataIndex: "referrerDrivenRevenue",
      sorter: (a, b) => a.referrerDrivenRevenue - b.referrerDrivenRevenue
    },
    {
      title: "Total Earnings",
      key: "referrerTotalEarnings",
      dataIndex: "referrerTotalEarnings",
      sorter: (a, b) => a.referrerTotalEarnings - b.referrerTotalEarnings
    },
    {
      title: "Total Payout",
      key: "totalPayoutAmount",
      dataIndex: "totalPayoutAmount",
      sorter: (a, b) => a.totalPayoutAmount - b.totalPayoutAmount
    },
    {
      title: "Outstanding Balance",
      key: "outstandingBalance",
      dataIndex: "outstandingBalance",
      sorter: (a, b) => a.outstandingBalance - b.outstandingBalance
    },
    {
      title: "Actions",
      key: "action",
      width: 50,

      render: (row) => (
        <div
          id="referral_row_actions"
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-around",
            gap: "10px",
            flexWrap: "wrap"
          }}
        >
          <>
            <Button
              disabled={!adminConfig?.referralDashboard?.hasPayoutViewAccess}
              type="primary"
              onClick={() => {
                setCurrentReferrerId(row.key);
                setIsViewPayoutModalOpen(true);
                fetchReferrerPayoutData(row.key);
              }}
            >
              View Payouts
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setCurrentReferrerBankDetails(row.bankAccountDetails);
                setIsBankDetailsModalOpen(true);
              }}
            >
              View Bank Details
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setCurrentReferrerId(row.key);
                fetchReferrerEventsData(row.key);
              }}
            >
              View Events
            </Button>
            <Button
              type="primary"
              disabled={!adminConfig?.referralDashboard?.hasPayoutCreateModifyAccess}
              onClick={() => {
                setMaxAmount(row.outstandingBalance);
                setCurrentReferrerId(row.key);
                setIsCreatePayoutModalOpen(true);
              }}
            >
              Create Payout
            </Button>
          </>
        </div>
      )
    }
  ];

  return (
    <div
      style={{
        marginTop: "20px",
        marginLeft: clamp(12, 30),
        marginRight: clamp(12, 30)
      }}
    >
      <Descriptions
        style={{ marginBottom: "30px" }}
        title="Referral Statistics"
        bordered
        column={{ xs: 1, sm: 2, md: 3 }}
      >
        <Descriptions.Item label="Referrers">{totalNumberOfReferrers}</Descriptions.Item>
        <Descriptions.Item label="Users gained via Referrals">
          {totalNumberOfReferredUser}
        </Descriptions.Item>
        <Descriptions.Item label="Courses Purchased">
          {totalNumberOfCoursesPurchasedViaReferral}
        </Descriptions.Item>
        <Descriptions.Item label="Revenue from Course Sales">
          {totalRevenueViaReferrals}
        </Descriptions.Item>
        <Descriptions.Item label="Total Payout Made">{totalPayOutToReferrers}</Descriptions.Item>
      </Descriptions>
      <Table
        columns={columns}
        dataSource={referrersList}
        bordered
        pagination={false}
        scroll={{ x: "max-content" }}
      />

      {isCreatePayoutModalOpen === true && (
        <CreatePayoutModal
          isModalOpen={isCreatePayoutModalOpen}
          setReferrerId={setCurrentReferrerId}
          maxAmount={maxAmount}
          setIsModalOpen={setIsCreatePayoutModalOpen}
          handleCreatePayout={handleCreatePayout}
        />
      )}

      {isViewPayoutModalOpen === true && (
        <ViewPayoutModal
          isModalOpen={isViewPayoutModalOpen}
          setIsModalOpen={setIsViewPayoutModalOpen}
          handleDeletePayout={handleDeletePayout}
          payoutData={currentReferrerPayouts}
          setPayoutData={setCurrentReferrerPayouts}
        />
      )}
      {isReferrerEventsModalOpen && (
        <ReferrerEventsViewModal
          isModalOpen={isReferrerEventsModalOpen}
          setIsModalOpen={setIsReferrerEventsModalOpen}
          referrerEventLogs={currentReferrerEventLogs}
        />
      )}

      {isBankDetailsModalOpen && (
        <ViewBankDetailsModal
          isModalOpen={isBankDetailsModalOpen}
          setIsModalOpen={setIsBankDetailsModalOpen}
          bankDetails={currentReferrerBankDetails}
        />
      )}
    </div>
  );
};

export default ReferralDash;
